import { useContext, useMemo, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronDown,
  faChevronRight,
} from "@fortawesome/free-solid-svg-icons";
import SearchBar from "components/SearchBar";
import budgetService, { DEFAULT_BUDGET_YEAR } from "api/budgetService";
import contractsService from "api/contractsService";
import registryInvoicesService from "api/registryInvoicesService";
import BudgetYearSelect from "components/Budget/BudgetYearSelect";
import MenuCell from "./Cells/MenuCell";
import BudgetExpensePreviewModal from "components/Budget/BudgetExpensePreviewModal";
import EditBudgetExpenseModal from "components/Budget/EditBudgetExpenseModal";
import ExpenseMetricSlide from "components/Budget/ExpenseMetricSlide";
import EngagementMetricSlide from "components/Budget/EngagementMetricSlide";
import InvoiceMetricSlide from "components/Budget/InvoiceMetricSlide";
import ContractMetricSlide from "components/Budget/ContractMetricSlide";
import InfoIcon from "assets/icons/info_icon.svg?react";
import ExpensesToolbar from "./ExpensesToolbar";
import {
  DEFAULT_EXPENSE_CRITERIA,
  ExpenseFilterCriteria,
} from "./ExpensesToolbar/types";
import { IBudgetExpense } from "dto/Budget/IBudgetExpense";
import { formatMoney } from "utils/formatMoney";
import ModalsManagerContext, {
  BudgetEngagementModalOptions,
  defaultValue,
} from "contexts/ModalsManager";
import styles from "./styles.module.scss";

// ── per-expense tree row ─────────────────────────────────────────────────────

const CATS = [
  { key: "eng", label: "Zaangażowania" },
  { key: "inv", label: "Faktury" },
  { key: "con", label: "Umowy" },
  { key: "tender", label: "Postępowania przetargowe" },
  { key: "invest", label: "Teczki inwestycji" },
] as const;

type CatKey = "eng" | "inv" | "con" | "tender" | "invest";

const ExpenseTreeRow = ({
  expense,
  isExpanded,
  onToggle,
  expandedCats,
  onToggleCat,
  onPreview,
  onEdit,
  onAddEngagement,
  onMetric,
  onItemMetric,
}: {
  expense: IBudgetExpense;
  isExpanded: boolean;
  onToggle: () => void;
  expandedCats: Set<string>;
  onToggleCat: (key: string) => void;
  onPreview: (e: IBudgetExpense) => void;
  onEdit: (e: IBudgetExpense) => void;
  onAddEngagement: (e: IBudgetExpense) => void;
  onMetric: (e: IBudgetExpense, settled: number) => void;
  onItemMetric: (type: CatKey, id: string) => void;
}) => {
  const { data: engagements = [] } = budgetService.useGetEngagements(
    expense.Id,
    isExpanded,
  );
  // Invoices are always fetched so that Rozliczone can be computed for collapsed rows too.
  const { data: invoices = [], isSuccess: invoicesLoaded } =
    budgetService.useGetInvoices(expense.Id, true);

  // Real invoices — virtual/computed entries (e.g. "Pozostało") have no invoice Number.
  const realInvoices = invoices.filter((inv) => !!inv.Number);

  // Sum shown in the Rozliczone column and as "Suma" inside the Faktury node.
  const settledAmount = invoicesLoaded
    ? realInvoices.reduce((s, inv) => s + (inv.Amount || 0), 0)
    : expense.SettledAmount;
  const { data: contracts = [] } = budgetService.useGetContracts(
    expense.Id,
    isExpanded,
  );
  const { data: tenders = [] } = budgetService.useGetTenders(
    expense.Id,
    isExpanded,
  );
  const { data: investmentCases = [] } = budgetService.useGetInvestmentCases(
    expense.Id,
    isExpanded,
  );

  const itemsOf = (key: CatKey) =>
    key === "eng"
      ? engagements
      : key === "inv"
      ? realInvoices
      : key === "con"
      ? contracts
      : key === "tender"
      ? tenders
      : investmentCases;

  return (
    <>
      {/* ── expense root row ── */}
      <div className={styles.expenseRow} onClick={onToggle}>
        <button
          type="button"
          className={styles.expandBtn}
          onClick={(e) => {
            e.stopPropagation();
            onToggle();
          }}
          aria-label={isExpanded ? "Zwiń" : "Rozwiń"}
        >
          <FontAwesomeIcon
            icon={isExpanded ? faChevronDown : faChevronRight}
            className={styles.chevron}
          />
        </button>
        <span className={styles.colChapter}>{expense.ChapterSymbol}</span>
        <span className={styles.colTitle}>{expense.Title}</span>
        <span className={styles.colMoney}>
          {formatMoney(expense.PlannedAmount)}
        </span>
        <span className={styles.colMoney}>
          {formatMoney(expense.EngagedAmount)}
        </span>
        <span className={styles.colMoney}>{formatMoney(settledAmount)}</span>
        <span className={styles.colMetric} onClick={(e) => e.stopPropagation()}>
          <span
            className={styles.metricBtn}
            title="Metryka"
            onClick={() => onMetric(expense, settledAmount)}
          >
            <InfoIcon style={{ width: 18, height: 18 }} />
          </span>
        </span>
        <span
          className={styles.colActions}
          onClick={(e) => e.stopPropagation()}
        >
          <MenuCell
            row={expense}
            onPreview={onPreview}
            onEdit={onEdit}
            onAddEngagement={onAddEngagement}
          />
        </span>
      </div>

      {/* ── category rows ── */}
      {isExpanded &&
        CATS.map(({ key, label }) => {
          const catKey = `${expense.Id}:${key}`;
          const catOpen = expandedCats.has(catKey);
          const items = itemsOf(key);

          return (
            <div key={catKey}>
              <div
                className={styles.categoryRow}
                onClick={() => onToggleCat(catKey)}
              >
                <span className={styles.catIndent} />
                <button type="button" className={styles.expandBtn}>
                  <FontAwesomeIcon
                    icon={catOpen ? faChevronDown : faChevronRight}
                    className={styles.chevronSm}
                  />
                </button>
                <span className={styles.catLabel}>{label}</span>
                <span className={styles.catCount}>({items.length})</span>
              </div>

              {/* ── item rows ── */}
              {catOpen && key !== "inv" && items.length === 0 && (
                <div className={styles.emptyItems}>Brak pozycji</div>
              )}

              {catOpen &&
                key === "eng" &&
                engagements.map((eng) => (
                  <div key={eng.Id} className={styles.itemRow}>
                    <span className={styles.itemSig}>{eng.Signature}</span>
                    <span className={styles.itemTitle}>{eng.Title}</span>
                    <span className={styles.itemMoney}>
                      {formatMoney(eng.Amount)}
                    </span>
                    <span className={styles.itemStatus}>
                      {eng.Status ?? ""}
                    </span>
                    <span
                      className={styles.itemMetric}
                      title="Metryka"
                      onClick={(e) => {
                        e.stopPropagation();
                        onItemMetric("eng", eng.Id);
                      }}
                    >
                      <InfoIcon style={{ width: 16, height: 16 }} />
                    </span>
                  </div>
                ))}

              {catOpen &&
                key === "inv" &&
                realInvoices.map((inv) => (
                  <div key={inv.Id} className={styles.itemRow}>
                    <span className={styles.itemSig}>{inv.Number}</span>
                    <span className={styles.itemTitleDouble}>
                      <span className={styles.itemTitleLine1}>
                        {inv.Sender}
                      </span>
                      <span className={styles.itemTitleLine2}>{inv.Title}</span>
                    </span>
                    <span className={styles.itemMoney}>
                      {formatMoney(inv.Amount)}
                    </span>
                    <span />
                    <span
                      className={styles.itemMetric}
                      title="Metryka"
                      onClick={(e) => {
                        e.stopPropagation();
                        onItemMetric("inv", inv.Id);
                      }}
                    >
                      <InfoIcon style={{ width: 16, height: 16 }} />
                    </span>
                  </div>
                ))}

              {catOpen &&
                key === "con" &&
                contracts.map((con) => (
                  <div key={con.Id} className={styles.itemRow}>
                    <span className={styles.itemSig} />
                    <span className={styles.itemTitleDouble}>
                      <span className={styles.itemTitleLine1}>
                        {con.Sender}
                      </span>
                      <span className={styles.itemTitleLine2}>
                        {con.Title ?? con.Subject}
                      </span>
                    </span>
                    <span className={styles.itemMoney}>
                      {formatMoney(con.Value)}
                    </span>
                    <span />
                    <span
                      className={styles.itemMetric}
                      title="Metryka"
                      onClick={(e) => {
                        e.stopPropagation();
                        onItemMetric("con", con.Id);
                      }}
                    >
                      <InfoIcon style={{ width: 16, height: 16 }} />
                    </span>
                  </div>
                ))}

              {catOpen &&
                key === "tender" &&
                tenders.map((t) => (
                  <div key={t.Id} className={styles.itemRow}>
                    <span className={styles.itemSig}>{t.Number}</span>
                    <span className={styles.itemTitle}>{t.Subject}</span>
                    <span className={styles.itemMoney}>
                      {formatMoney(t.Amount)}
                    </span>
                    <span className={styles.itemStatus}>{t.Status ?? ""}</span>
                    <span />
                  </div>
                ))}

              {catOpen &&
                key === "invest" &&
                investmentCases.map((ic) => (
                  <div key={ic.Id} className={styles.itemRow}>
                    <span className={styles.itemSig}>{ic.Signature}</span>
                    <span className={styles.itemTitle}>{ic.Title}</span>
                    <span />
                    <span />
                    <span />
                  </div>
                ))}
            </div>
          );
        })}
    </>
  );
};

// ── ładowarki metryk pozycji (dociągają pełny rekord 1:1 jak w rejestrze) ──────

// Zaangażowanie: 1:1 jak w rejestrze „Zaangażowania" — to samo źródło (lista roku),
// rekord po Id; FE-only, bez dodatkowego BE.
const EngagementMetricLoader = ({
  id,
  year,
  onClose,
}: {
  id: string;
  year: number;
  onClose: () => void;
}) => {
  const { data: all = [] } = budgetService.useGetAllEngagements(year);
  const eng = all.find((e) => e.Id === id);
  if (!eng) return null;
  return <EngagementMetricSlide engagement={eng} onClose={onClose} />;
};

// Faktura/Umowa: dociągane po Id (BE rozwiązuje Id pozycji budżetu na bogaty
// rekord rejestrowy — patrz docs/backend-budget-item-metric.md).
// Faktura: to samo źródło co rejestr „Faktury i rachunki" (registryInvoicesService)
// po Id — gwarancja tożsamości danych metryki w obu lokalizacjach.
const InvoiceMetricLoader = ({
  id,
  onClose,
}: {
  id: string;
  onClose: () => void;
}) => {
  const { data } = registryInvoicesService.useGetInvoice(id);
  if (!data) return null;
  return <InvoiceMetricSlide invoice={data} onClose={onClose} />;
};

// Umowa: to samo źródło co rejestr „Umowy" (contractsService) po Id —
// gwarancja tożsamości danych metryki w obu lokalizacjach.
const ContractMetricLoader = ({
  id,
  onClose,
}: {
  id: string;
  onClose: () => void;
}) => {
  const { data } = contractsService.useGetContract(id);
  if (!data) return null;
  return <ContractMetricSlide contract={data} onClose={onClose} />;
};

// ── main module ──────────────────────────────────────────────────────────────

const BudgetModule = () => {
  const { setModals, setOptions } = useContext(ModalsManagerContext);
  const [year, setYear] = useState(DEFAULT_BUDGET_YEAR);
  const [search, setSearch] = useState("");
  const [previewExpense, setPreviewExpense] = useState<IBudgetExpense | null>(
    null,
  );
  const [editExpense, setEditExpense] = useState<IBudgetExpense | null>(null);
  const [metricOf, setMetricOf] = useState<{
    expense: IBudgetExpense;
    settled: number;
  } | null>(null);
  // Metryka pojedynczej pozycji (zaangażowanie/faktura/umowa) — po Id.
  const [itemMetric, setItemMetric] = useState<{
    type: CatKey;
    id: string;
  } | null>(null);

  const openEngagementModal = (expense: IBudgetExpense) => {
    const opts = new BudgetEngagementModalOptions();
    opts.initialExpenseId = expense.Id;
    setOptions(opts);
    setModals({ ...defaultValue, createBudgetEngagement: true, hide: false });
  };

  const [expandedExpenses, setExpandedExpenses] = useState<Set<string>>(
    new Set(),
  );
  const [expandedCats, setExpandedCats] = useState<Set<string>>(new Set());

  // Jedna firma na użytkownika, bez przełącznika kontekstu (decyzja D1) — nie ma już
  // zdarzenia "zmiana firmy" w trakcie sesji, więc czyszczenie modali/rozwinięć po takiej
  // zmianie jest zbędne (dawne useOnProfileChange nigdy by się tu nie odpaliło).
  const [criteria, setCriteria] = useState<ExpenseFilterCriteria>(
    DEFAULT_EXPENSE_CRITERIA,
  );

  const { data: expenses = [], isLoading } = budgetService.useGetExpenses(year);

  const chapters = useMemo(
    () =>
      Array.from(
        new Set(
          expenses.map((e) => e.ChapterSymbol).filter((c): c is string => !!c),
        ),
      ).sort(),
    [expenses],
  );

  const filteredExpenses = useMemo(() => {
    const q = search.trim().toLowerCase();
    return expenses.filter((e) => {
      if (
        q &&
        !(
          e.Title.toLowerCase().includes(q) ||
          e.ChapterSymbol?.toLowerCase().includes(q)
        )
      )
        return false;

      if (criteria.chapter !== "all" && e.ChapterSymbol !== criteria.chapter)
        return false;

      if (criteria.settlement !== "all") {
        const planned = e.PlannedAmount ?? 0;
        const settled = e.SettledAmount ?? 0;
        const fully = planned > 0 && settled >= planned;
        const partial = settled > 0 && settled < planned;
        const none = settled <= 0;
        if (criteria.settlement === "settled" && !fully) return false;
        if (criteria.settlement === "partial" && !partial) return false;
        if (criteria.settlement === "unsettled" && !none) return false;
      }

      if (criteria.engagement !== "all") {
        const engaged = (e.EngagedAmount ?? 0) > 0;
        if (criteria.engagement === "engaged" && !engaged) return false;
        if (criteria.engagement === "notengaged" && engaged) return false;
      }

      return true;
    });
  }, [expenses, search, criteria]);

  const toggleExpense = (id: string) =>
    setExpandedExpenses((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const toggleCat = (key: string) =>
    setExpandedCats((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });

  return (
    <div className={styles.wrapper}>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <span className={styles.title}>Lista wydatków</span>
          <BudgetYearSelect
            value={year}
            onChange={setYear}
            className={styles.yearSelect}
          />
        </div>
        <div className={styles.searchContainer}>
          <SearchBar isExpanded={true} onChange={(text) => setSearch(text)} />
        </div>
      </div>

      <ExpensesToolbar
        chapters={chapters}
        criteria={criteria}
        setCriteria={setCriteria}
      />

      <div className={styles.treeContainer}>
        {/* header */}
        <div className={styles.tableHeader}>
          <span /> {/* chevron */}
          <span>Rozdział</span>
          <span>Tytuł</span>
          <span className={styles.headerRight}>Budżet zadania</span>
          <span className={styles.headerRight}>Zaangażowane</span>
          <span className={styles.headerRight}>Rozliczone</span>
          <span style={{ textAlign: "center" }}>Met.</span>
          <span />
        </div>

        {isLoading && <div className={styles.loading}>Ładowanie…</div>}

        {!isLoading && expenses.length === 0 && (
          <div className={styles.empty}>Brak wydatków dla wybranego roku.</div>
        )}

        {filteredExpenses.map((expense) => (
          <ExpenseTreeRow
            key={expense.Id}
            expense={expense}
            isExpanded={expandedExpenses.has(expense.Id)}
            onToggle={() => toggleExpense(expense.Id)}
            expandedCats={expandedCats}
            onToggleCat={toggleCat}
            onPreview={setPreviewExpense}
            onEdit={setEditExpense}
            onAddEngagement={openEngagementModal}
            onMetric={(e, settled) => setMetricOf({ expense: e, settled })}
            onItemMetric={(type, id) => setItemMetric({ type, id })}
          />
        ))}
      </div>

      {metricOf && (
        <ExpenseMetricSlide
          expense={metricOf.expense}
          settledAmount={metricOf.settled}
          onClose={() => setMetricOf(null)}
        />
      )}

      {itemMetric?.type === "eng" && (
        <EngagementMetricLoader
          id={itemMetric.id}
          year={year}
          onClose={() => setItemMetric(null)}
        />
      )}
      {itemMetric?.type === "inv" && (
        <InvoiceMetricLoader
          id={itemMetric.id}
          onClose={() => setItemMetric(null)}
        />
      )}
      {itemMetric?.type === "con" && (
        <ContractMetricLoader
          id={itemMetric.id}
          onClose={() => setItemMetric(null)}
        />
      )}

      {previewExpense && (
        <BudgetExpensePreviewModal
          expense={previewExpense}
          onClose={() => setPreviewExpense(null)}
        />
      )}
      {editExpense && (
        <EditBudgetExpenseModal
          expense={editExpense}
          onClose={() => setEditExpense(null)}
        />
      )}
    </div>
  );
};

export default BudgetModule;
